/**
 * @fileoverview HelpMenuExternalItem — external-link row variant inside the
 * HelpPopover. Composes three siblings: a MenuItem with the label/icon, a
 * vertical bp6-divider, and a minimal `arrow-top-right` AnchorButton. Both
 * the label-click and the arrow-click navigate to the same href in a new tab;
 * the arrow is the explicit external-link affordance.
 *
 * Foundry mapping: the help-menu rows that point at external URLs (Platform
 * documentation, Status page) — a `bp6-menu-item` followed by a `bp6-divider`
 * and a small `arrow-top-right` minimal anchor-button.
 */

import { AnchorButton, Divider, MenuItem } from "@blueprintjs/core";
import type { ReactElement } from "react";

export interface HelpMenuExternalItemProps {
  label: string;
  /** A rendered icon element — typed as `ReactElement` to satisfy
   *  Blueprint's `MaybeElement` shape for `MenuItem.icon`. */
  icon: ReactElement;
  href: string;
}

export function HelpMenuExternalItem({
  label,
  icon,
  href,
}: HelpMenuExternalItemProps): ReactElement {
  return (
    <div data-testid="help-menu-external-item" className="bp-help-menu-external-row">
      <MenuItem
        text={label}
        icon={icon}
        href={href}
        target="_blank"
        rel="noreferrer"
        shouldDismissPopover={true}
      />
      <Divider />
      <AnchorButton
        minimal
        small
        icon="arrow-top-right"
        href={href}
        target="_blank"
        rel="noreferrer"
        aria-label={`${label} (opens in new tab)`}
        title={`${label} (opens in new tab)`}
        className="bp-help-menu-external-button"
      />
    </div>
  );
}
